import moment from "moment";
import { type TFunction } from "i18next";
import TravelIndustry from "enums/TravelIndustry";
import { type Amount, formatAmount } from "utils/formatCash";

// Network codes that read badly once they are title cased.
const ENUM_OVERRIDES: Record<string, string> = {
  SUV: "SUV",
  VAT: "VAT",
  WIFI: "WiFi",
};

export type FlightSegment = {
  travelDate?: number | null;
  carrierCode?: string | null;
  flightNumber?: string | null;
  serviceClass?: string | null;
  fareBasisCode?: string | null;
  departureAirportCode?: string | null;
  arrivalAirportCode?: string | null;
  fare?: Amount | null;
};

export type AncillaryFee = {
  feeType?: string | null;
  amount?: Amount | null;
};

export type AirlineTravelData = {
  ticketNumber?: string | null;
  recordLocator?: string | null;
  passengerName?: string | null;
  issuingCarrier?: string | null;
  ticketIssueDate?: number | null;
  travelAgencyName?: string | null;
  travelAgencyCode?: string | null;
  totalFare?: Amount | null;
  totalFees?: Amount | null;
  totalTaxes?: Amount | null;
  segments?: Array<FlightSegment | null> | null;
  ancillaryFees?: Array<AncillaryFee | null> | null;
};

export type LodgingTravelData = {
  folioNumber?: string | null;
  checkInDate?: number | null;
  checkOutDate?: number | null;
  nights?: number | null;
  roomRate?: Amount | null;
  roomTax?: Amount | null;
  foodBeverageCharges?: Amount | null;
  nonRoomCharges?: Amount | null;
  prepaidExpenses?: Amount | null;
  totalTax?: Amount | null;
  totalCharges?: Amount | null;
};

export type CarRentalTravelData = {
  agreementNumber?: string | null;
  vehicleClass?: string | null;
  rateFrequency?: string | null;
  rentalRate?: Amount | null;
  weeklyRate?: Amount | null;
  daysRented?: number | null;
  pickupDate?: number | null;
  returnDate?: number | null;
  pickupCity?: string | null;
  returnCity?: string | null;
  insuranceCharges?: Amount | null;
  fuelCharges?: Amount | null;
  oneWayDropOffCharges?: Amount | null;
  totalTax?: Amount | null;
};

export type RailTravelData = {
  ticketNumber?: string | null;
  travelDate?: number | null;
  departureStation?: string | null;
  arrivalStation?: string | null;
  ticketClass?: string | null;
  numberOfAdults?: number | null;
  numberOfChildren?: number | null;
  serviceAmount?: Amount | null;
};

export type TravelData = {
  [TravelIndustry.AIRLINE]?: AirlineTravelData | null;
  [TravelIndustry.LODGING]?: LodgingTravelData | null;
  [TravelIndustry.CAR_RENTAL]?: CarRentalTravelData | null;
  [TravelIndustry.RAIL]?: RailTravelData | null;
};

export type TravelFact = { label: string; value: string };

export type TravelRow = { key: string; label: string; value: string };

export type TravelSegmentRow = {
  key: string;
  date: string;
  flight: string;
  fareBasisCode: string;
  route: string;
  fare: string;
};

export const TRAVEL_INDUSTRY_ORDER: TravelIndustry[] = [
  TravelIndustry.AIRLINE,
  TravelIndustry.LODGING,
  TravelIndustry.CAR_RENTAL,
  TravelIndustry.RAIL,
];

// Section titles, as i18n keys.
export const INDUSTRY_TITLE: Record<TravelIndustry, string> = {
  [TravelIndustry.AIRLINE]: "Airline",
  [TravelIndustry.LODGING]: "Lodging",
  [TravelIndustry.CAR_RENTAL]: "Car Rental",
  [TravelIndustry.RAIL]: "Rail",
};

// Presence requires renderable content, not just the industry object: backfills
// can leave an airline object holding nothing beyond its identifiers.
export const presentIndustries = (
  t: TFunction,
  travelData?: TravelData | null,
): TravelIndustry[] =>
  TRAVEL_INDUSTRY_ORDER.filter(
    (industry) =>
      buildIndustryFacts(t, industry, travelData).length > 0 ||
      industryHasDetails(t, industry, travelData),
  );

// The identifiers that stay visible in an industry's collapsed summary.
export const buildIndustryFacts = (
  t: TFunction,
  industry: TravelIndustry,
  travelData?: TravelData | null,
): TravelFact[] => {
  switch (industry) {
    case TravelIndustry.AIRLINE:
      return compactFacts([
        {
          label: t("Locator"),
          value: travelData?.airline?.recordLocator ?? "",
        },
        {
          label: t("Ticket Number"),
          value: travelData?.airline?.ticketNumber ?? "",
        },
      ]);
    case TravelIndustry.LODGING:
      return compactFacts([
        {
          label: t("Folio Number"),
          value: travelData?.lodging?.folioNumber ?? "",
        },
      ]);
    case TravelIndustry.CAR_RENTAL:
      return compactFacts([
        {
          label: t("Agreement Number"),
          value: travelData?.carRental?.agreementNumber ?? "",
        },
      ]);
    case TravelIndustry.RAIL:
      return compactFacts([
        {
          label: t("Ticket Number"),
          value: travelData?.rail?.ticketNumber ?? "",
        },
      ]);
    default:
      return [];
  }
};

export const humanizeTravelEnum = (
  t: TFunction,
  raw?: string | null,
): string => {
  if (!raw) return "";

  const words = raw
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => ENUM_OVERRIDES[word.toUpperCase()] ?? capitalize(word))
    .join(" ");

  return t(words);
};

// Travel dates carry no time or zone, so they are formatted in UTC to keep the day the
// merchant reported.
export const formatTravelDate = (value?: number | null): string =>
  value === undefined || value === null
    ? ""
    : moment.utc(value).format("MM/DD/YYYY");

export const formatSegmentRoute = (segment: FlightSegment): string =>
  [segment.departureAirportCode, segment.arrivalAirportCode]
    .filter(Boolean)
    .join(" to ");

export const formatFlightNumber = (segment: FlightSegment): string =>
  [segment.carrierCode, segment.flightNumber].filter(Boolean).join(" ");

export const buildAirlineRows = (
  t: TFunction,
  airline: AirlineTravelData,
): TravelRow[] =>
  compactRows([
    {
      key: "issuingCarrier",
      label: t("Issuing Carrier"),
      value: airline.issuingCarrier ?? "",
    },
    {
      key: "ticketIssueDate",
      label: t("Issued"),
      value: formatTravelDate(airline.ticketIssueDate),
    },
    {
      key: "travelAgencyName",
      label: t("Travel Agency"),
      value: airline.travelAgencyName ?? "",
    },
    {
      key: "travelAgencyCode",
      label: t("Agency Code"),
      value: airline.travelAgencyCode ?? "",
    },
  ]);

// Rendered below the itemized ancillary fees so the grand total reads after
// its components.
export const buildAirlineTotalRows = (
  t: TFunction,
  airline: AirlineTravelData,
): TravelRow[] =>
  compactRows([
    {
      key: "totalFees",
      label: t("Total Fees"),
      value: formatAmount(airline.totalFees),
    },
    {
      key: "totalTaxes",
      label: t("Total Taxes"),
      value: formatAmount(airline.totalTaxes),
    },
    {
      key: "totalFare",
      label: t("Total Fare"),
      value: formatAmount(airline.totalFare),
    },
  ]);

export const buildSegmentRows = (
  t: TFunction,
  airline: AirlineTravelData,
): TravelSegmentRow[] =>
  (airline.segments ?? []).filter(isPresent).map((segment, index) => ({
    key: String(index),
    date: formatTravelDate(segment.travelDate),
    flight: formatFlightNumber(segment),
    fareBasisCode: segment.fareBasisCode ?? "",
    route: formatSegmentRoute(segment),
    fare: formatAmount(segment.fare),
  }));

export const buildAncillaryFeeRows = (
  t: TFunction,
  airline: AirlineTravelData,
): TravelRow[] =>
  (airline.ancillaryFees ?? []).filter(isPresent).map((fee, index) => ({
    key: String(index),
    label: humanizeTravelEnum(t, fee.feeType) || t("Fee"),
    value: formatAmount(fee.amount),
  }));

export const buildLodgingRows = (
  t: TFunction,
  lodging: LodgingTravelData,
): TravelRow[] =>
  compactRows([
    {
      key: "checkInDate",
      label: t("Check-in"),
      value: formatTravelDate(lodging.checkInDate),
    },
    {
      key: "checkOutDate",
      label: t("Check-out"),
      value: formatTravelDate(lodging.checkOutDate),
    },
    { key: "nights", label: t("Nights"), value: formatCount(lodging.nights) },
    {
      key: "roomRate",
      label: t("Room Rate"),
      value: formatAmount(lodging.roomRate),
    },
    {
      key: "roomTax",
      label: t("Room Tax"),
      value: formatAmount(lodging.roomTax),
    },
    {
      key: "foodBeverageCharges",
      label: t("Food and Beverage"),
      value: formatAmount(lodging.foodBeverageCharges),
    },
    {
      key: "nonRoomCharges",
      label: t("Other Charges"),
      value: formatAmount(lodging.nonRoomCharges),
    },
    {
      key: "prepaidExpenses",
      label: t("Prepaid"),
      value: formatAmount(lodging.prepaidExpenses),
    },
    {
      key: "totalTax",
      label: t("Total Tax"),
      value: formatAmount(lodging.totalTax),
    },
    {
      key: "totalCharges",
      label: t("Total Charges"),
      value: formatAmount(lodging.totalCharges),
    },
  ]);

export const buildCarRentalRows = (
  t: TFunction,
  carRental: CarRentalTravelData,
): TravelRow[] =>
  compactRows([
    {
      key: "vehicleClass",
      label: t("Vehicle Class"),
      value: humanizeTravelEnum(t, carRental.vehicleClass),
    },
    {
      key: "daysRented",
      label: t("Days Rented"),
      value: formatCount(carRental.daysRented),
    },
    {
      key: "pickupDate",
      label: t("Pickup Date"),
      value: formatTravelDate(carRental.pickupDate),
    },
    {
      key: "returnDate",
      label: t("Return Date"),
      value: formatTravelDate(carRental.returnDate),
    },
    {
      key: "pickupCity",
      label: t("Pickup City"),
      value: carRental.pickupCity ?? "",
    },
    {
      key: "returnCity",
      label: t("Return City"),
      value: carRental.returnCity ?? "",
    },
    {
      key: "rentalRate",
      label: t("Rate"),
      value: formatRentalRate(t, carRental),
    },
    {
      key: "weeklyRate",
      label: t("Weekly Rate"),
      value: formatAmount(carRental.weeklyRate),
    },
    {
      key: "insuranceCharges",
      label: t("Insurance"),
      value: formatAmount(carRental.insuranceCharges),
    },
    {
      key: "fuelCharges",
      label: t("Fuel"),
      value: formatAmount(carRental.fuelCharges),
    },
    {
      key: "oneWayDropOffCharges",
      label: t("One Way Drop Off"),
      value: formatAmount(carRental.oneWayDropOffCharges),
    },
    {
      key: "totalTax",
      label: t("Total Tax"),
      value: formatAmount(carRental.totalTax),
    },
  ]);

export const buildRailRows = (
  t: TFunction,
  rail: RailTravelData,
): TravelRow[] =>
  compactRows([
    {
      key: "travelDate",
      label: t("Travel Date"),
      value: formatTravelDate(rail.travelDate),
    },
    {
      key: "departureStation",
      label: t("From"),
      value: rail.departureStation ?? "",
    },
    { key: "arrivalStation", label: t("To"), value: rail.arrivalStation ?? "" },
    {
      key: "ticketClass",
      label: t("Class"),
      value: humanizeTravelEnum(t, rail.ticketClass),
    },
    {
      key: "numberOfAdults",
      label: t("Adults"),
      value: formatCount(rail.numberOfAdults),
    },
    {
      key: "numberOfChildren",
      label: t("Children"),
      value: formatCount(rail.numberOfChildren),
    },
    {
      key: "serviceAmount",
      label: t("Amount"),
      value: formatAmount(rail.serviceAmount),
    },
  ]);

// Whether an industry has anything to expand beyond its summary facts. A
// facts-only industry renders as a plain block instead of an accordion.
export function industryHasDetails(
  t: TFunction,
  industry: TravelIndustry,
  travelData?: TravelData | null,
): boolean {
  switch (industry) {
    case TravelIndustry.AIRLINE: {
      const airline = travelData?.airline;
      return (
        !!airline &&
        (buildAirlineRows(t, airline).length > 0 ||
          buildAirlineTotalRows(t, airline).length > 0 ||
          buildSegmentRows(t, airline).length > 0 ||
          buildAncillaryFeeRows(t, airline).length > 0)
      );
    }
    case TravelIndustry.LODGING: {
      const lodging = travelData?.lodging;
      return !!lodging && buildLodgingRows(t, lodging).length > 0;
    }
    case TravelIndustry.CAR_RENTAL: {
      const carRental = travelData?.carRental;
      return !!carRental && buildCarRentalRows(t, carRental).length > 0;
    }
    case TravelIndustry.RAIL: {
      const rail = travelData?.rail;
      return !!rail && buildRailRows(t, rail).length > 0;
    }
    default:
      return false;
  }
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function isPresent<T>(value: T | null | undefined): value is T {
  return !!value;
}

function compactFacts(facts: TravelFact[]): TravelFact[] {
  return facts.filter((fact) => !!fact.value);
}

function compactRows(rows: TravelRow[]): TravelRow[] {
  return rows.filter((row) => !!row.value);
}

function formatCount(value?: number | null): string {
  return value === undefined || value === null ? "" : String(value);
}

function formatRentalRate(
  t: TFunction,
  carRental: CarRentalTravelData,
): string {
  const rate = formatAmount(carRental.rentalRate);
  if (!rate) return "";

  const frequency = humanizeTravelEnum(t, carRental.rateFrequency);
  return frequency ? `${rate} / ${frequency}` : rate;
}
